import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { shootCoinsFrom, playCoins, playExplosion, playCelebration } from "@/lib/confetti";
import CoinStreakBanner from "@/components/CoinStreakBanner";

function animateNumber(set, from, to, ms) {
  const start = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - start) / ms);
    set(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function Dragon({ smug, flap }) {
  return (
    <svg width="150" height="120" viewBox="0 0 150 120" style={{ overflow: "visible" }}>
      <g>
        {/* wings */}
        <motion.path d="M62 48 q-30 -34 -54 -22 q16 6 22 20 q-14 0 -20 6 q20 12 40 6z" fill="#2E7D32" stroke="#111" strokeWidth="1.6"
          animate={flap ? { rotate: [0, -16, 0] } : { rotate: 0 }} transition={{ duration: 0.5, repeat: flap ? Infinity : 0 }} style={{ transformOrigin: "62px 48px" }} />
        {/* tail */}
        <path d="M28 84 q-22 6 -24 22 q14 -4 22 -10" fill="#388E3C" stroke="#111" strokeWidth="1.6" />
        {/* body */}
        <ellipse cx="62" cy="78" rx="36" ry="26" fill="#43A047" stroke="#111" strokeWidth="2" />
        <ellipse cx="66" cy="86" rx="22" ry="14" fill="#C5E1A5" />
        {/* head */}
        <g transform={smug ? "rotate(6 104 46)" : "none"}>
          <ellipse cx="104" cy="46" rx="24" ry="19" fill="#43A047" stroke="#111" strokeWidth="2" />
          <path d="M124 40 q14 2 16 8 q-10 4 -16 2z" fill="#43A047" stroke="#111" strokeWidth="1.6" />
          <circle cx="104" cy="40" r="4" fill="#111" />
          {smug ? <path d="M96 54 q10 6 22 0" stroke="#111" strokeWidth="2.4" fill="none" strokeLinecap="round" />
                : <path d="M98 55 q9 3 18 0" stroke="#111" strokeWidth="2.2" fill="none" strokeLinecap="round" />}
          <path d="M88 32 l6 -12 6 10 M102 28 l5 -11 6 10" fill="none" stroke="#2E7D32" strokeWidth="5" strokeLinecap="round" />
        </g>
        {/* legs */}
        <path d="M46 100 l-3 12 M76 100 l3 12" stroke="#2E7D32" strokeWidth="9" strokeLinecap="round" />
      </g>
    </svg>
  );
}

// Dragon Hoard: a dragon swoops onto the coin pile, breathes a stream of fire that
// turns into gold coins raining down, then curls up smugly on the grown hoard.
export default function DragonHoardCelebration({
  coinsAwarded, totalCoins, scorePercent,
  canAdvance, onRetry, onContinue, soundOn = true, hasPrev = false, onPrev, onDone,
}) {
  const prevTotal = Math.max(0, (totalCoins || 0) - (coinsAwarded || 0));
  const [phase, setPhase] = useState("swoop"); // swoop -> breathe -> hoard -> smug -> done
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(prevTotal);
  const timers = useRef([]);
  const sceneRef = useRef(null);
  const showScore = ["hoard", "smug", "done"].includes(phase);
  const breathing = phase === "breathe";

  const burst = (xr, yr) => {
    const r = sceneRef.current?.getBoundingClientRect();
    if (!r) return;
    shootCoinsFrom((r.left + r.width * xr) / window.innerWidth, (r.top + r.height * yr) / window.innerHeight);
  };

  useEffect(() => {
    const add = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    add(() => { setPhase("breathe"); if (soundOn) playCelebration(); }, 1900);
    // fire stream turns into coin bursts sweeping across the pile
    for (let i = 0; i < 6; i++) add(() => { burst(0.68 + (i % 3) * 0.08, 0.62); if (soundOn && i % 2 === 0) playCoins(5); }, 2300 + i * 430);
    add(() => {
      setPhase("hoard");
      if (soundOn) playExplosion();
      burst(0.5, 0.6); burst(0.65, 0.55);
      animateNumber(setCount, 0, coinsAwarded, 1400);
      animateNumber(setTotal, prevTotal, totalCoins, 1600);
    }, 5200);
    add(() => { setPhase("smug"); if (soundOn) playCoins(7); }, 7000);
    add(() => { setPhase("done"); onDone && onDone(); }, 8400);
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="text-center py-2 relative scroll-mt-24" data-testid="dragon-celebration">
      <div ref={sceneRef} className="relative mx-auto w-full max-w-md h-60 overflow-hidden rounded-2xl"
        style={{ background: "linear-gradient(180deg,#1B0E2E 0%,#31184A 55%,#120A1E 100%)" }} data-testid="dragon-scene">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 65% 45% at 50% 30%, rgba(255,158,0,0.16), transparent 70%)" }} />
        <div className="absolute left-5 top-7 font-display font-extrabold text-2xl select-none" style={{ color: "#FFD600", opacity: 0.22, transform: "rotate(-7deg)" }}>HOARD</div>
        {/* coin pile grows with the hoard */}
        <motion.div className="absolute left-0 right-0 mx-auto" style={{ bottom: -12, width: 300 }}
          animate={{ scale: showScore ? 1.18 : 1 }} transition={{ duration: 0.8 }}>
          <svg width="300" height="72" viewBox="0 0 300 72">
            <ellipse cx="150" cy="58" rx="140" ry="20" fill="#B8860B" />
            <ellipse cx="150" cy="50" rx="112" ry="20" fill="#DAA520" />
            <ellipse cx="150" cy="42" rx="80" ry="18" fill="#FFD600" />
            {[40, 85, 130, 175, 220, 255].map((x, i) => (
              <circle key={i} cx={x} cy={36 + (i % 3) * 7} r="7" fill="#FFC400" stroke="#B8860B" strokeWidth="2" />
            ))}
          </svg>
        </motion.div>
        {/* fire stream that becomes coins */}
        {breathing && (
          <motion.div className="absolute" style={{ left: "52%", top: 86 }}
            initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 1, 0.6] }} transition={{ duration: 0.6 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.span key={i} className="absolute rounded-full" style={{ width: 22 - i * 2, height: 22 - i * 2, left: i * 26, top: i * 14, background: i < 2 ? "#FF6D00" : i < 4 ? "#FFC400" : "#FFD600", boxShadow: "0 0 14px #FF9E00" }}
                animate={{ scale: [0.7, 1.25, 0.8], opacity: [0.6, 1, 0.6] }} transition={{ duration: 0.4, repeat: Infinity, delay: i * 0.07 }} />
            ))}
          </motion.div>
        )}
        {/* the dragon */}
        <motion.div className="absolute" style={{ left: "8%", width: 150, height: 120 }}
          initial={{ x: -360, y: -110 }}
          animate={phase === "swoop" ? { x: 0, y: 26, transition: { duration: 1.8, ease: "easeOut" } } : { x: 0, y: phase === "smug" || phase === "done" ? 44 : 30 }}
          data-testid="dragon-character">
          <Dragon smug={["smug", "done"].includes(phase)} flap={phase === "swoop"} />
        </motion.div>
        {["smug", "done"].includes(phase) && (
          <motion.div className="absolute font-display font-bold select-none" style={{ left: "24%", top: 46, color: "#FFD600", fontSize: 22 }}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="dragon-smug-zzz">
            MINE. ✨
          </motion.div>
        )}
      </div>

      <div className={`flex flex-col items-center gap-1.5 mt-2 transition-opacity duration-500 ${showScore ? "opacity-100" : "opacity-0"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }} data-testid="quiz-xp-awarded">
          <span className="text-2xl">🪙</span>
          <span className="text-xl sm:text-2xl leading-none">+{count.toLocaleString()} coins today</span>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }} data-testid="coin-grand-total">
          <span className="text-2xl">🪙</span>
          <span className="text-3xl leading-none text-outline-lime">{total.toLocaleString()}</span>
          <span className="text-xl leading-none">total</span>
        </div>
        {scorePercent != null && <div className="text-3xl font-extrabold font-display text-[#8fb300]" data-testid="quiz-score">{scorePercent}%</div>}
      </div>

      {showScore && <CoinStreakBanner celebrationsEnabled={soundOn} compact className="mt-2 max-w-md mx-auto justify-center text-center" />}

      <AnimatePresence>
        {phase === "done" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="coin-done" className="scroll-mb-24">
            <p className="text-black font-semibold max-w-md mx-auto mt-2" data-testid="quiz-encouragement">
              The hoard grows — dragons never stop stacking!
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {hasPrev && <button data-testid="quiz-prev-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={() => onPrev && onPrev()}>← Prev</button>}
              {onRetry && <button data-testid="quiz-retry-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={onRetry}>Redo</button>}
              {canAdvance && onContinue && <button data-testid="quiz-continue-button" className="btn-primary text-sm px-4 py-2 whitespace-nowrap" onClick={onContinue}>Next →</button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
